import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  model,
  viewChild,
} from '@angular/core';
import { UI_TEXT } from '../i18n';
import { ScFieldHover } from './field-hover.directive';

/** Lo que se puede escribir: dígitos, un signo al comienzo y una coma o punto decimal. */
function sanitize(text: string): string {
  let out = '';
  let decimal = false;
  for (const char of text) {
    if (/\d/.test(char)) out += char;
    else if (char === '-' && out === '') out += char;
    else if ((char === '.' || char === ',') && !decimal) {
      decimal = true;
      out += '.';
    }
  }
  return out;
}

/**
 * Campo numérico del kit (reemplaza a dx-number-box). Como el de DevExtreme: no deja escribir
 * letras, al salir del campo ajusta el valor a `min` y `max` (vacío es null), y los botones y las
 * flechas ↑ ↓ dan un paso de `step` y avisan de inmediato. Sólo avisa los cambios de la persona.
 */
@Component({
  selector: 'sc-number-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [ScFieldHover],
  host: {
    class: 'sck-field sck-number',
    '[class.sck-field--disabled]': 'disabled()',
  },
  template: `
    <div class="sck-field__control">
      <input
        #field
        class="sck-field__input"
        type="text"
        inputmode="decimal"
        role="spinbutton"
        autocomplete="off"
        [value]="value() ?? ''"
        [disabled]="disabled()"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-valuemin]="min()"
        [attr.aria-valuemax]="max()"
        [attr.aria-valuenow]="value()"
        (input)="onInput()"
        (change)="commit()"
        (keydown.arrowup)="$event.preventDefault(); spin(1)"
        (keydown.arrowdown)="$event.preventDefault(); spin(-1)"
      />
    </div>
    @if (showSpinButtons()) {
      <div class="sck-number__spin">
        <button
          type="button"
          class="sck-number__spin-button sck-number__spin--up"
          tabindex="-1"
          [attr.aria-label]="text.increase"
          (mousedown)="$event.preventDefault()"
          (click)="spin(1)"
        >
          <span class="material-icons sck-icon" aria-hidden="true" data-icon="arrow_drop_up"></span>
        </button>
        <button
          type="button"
          class="sck-number__spin-button sck-number__spin--down"
          tabindex="-1"
          [attr.aria-label]="text.decrease"
          (mousedown)="$event.preventDefault()"
          (click)="spin(-1)"
        >
          <span
            class="material-icons sck-icon"
            aria-hidden="true"
            data-icon="arrow_drop_down"
          ></span>
        </button>
      </div>
    }
  `,
})
export class ScNumberInputComponent {
  readonly value = model<number | null>(null);
  readonly min = input<number | null>(null);
  readonly max = input<number | null>(null);
  readonly step = input(1);
  readonly showSpinButtons = input(false);
  readonly disabled = input(false);
  readonly ariaLabel = input<string | null>(null);

  protected readonly text = UI_TEXT;
  private readonly field = viewChild.required<ElementRef<HTMLInputElement>>('field');

  protected onInput(): void {
    const el = this.field().nativeElement;
    const clean = sanitize(el.value);
    if (clean !== el.value) el.value = clean;
  }

  /** Al salir del campo (o Enter): número ajustado a los límites, o null si quedó vacío. */
  protected commit(): void {
    this.set(this.typed());
  }

  /** Como DevExtreme, el giro parte de lo escrito aunque aún no se haya confirmado. */
  protected spin(direction: 1 | -1): void {
    this.set((this.typed() ?? this.value() ?? 0) + direction * this.step());
  }

  /** El número escrito en el campo, o null si está vacío o sin número. */
  private typed(): number | null {
    const text = sanitize(this.field().nativeElement.value);
    if (text === '' || text === '-' || text === '.') return null;
    const parsed = Number(text);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private set(next: number | null): void {
    const value = next === null ? null : this.clamp(next);
    // El campo muestra siempre el valor ajustado, aunque no cambie (200 con máximo 168 → 168).
    this.field().nativeElement.value = value === null ? '' : String(value);
    if (value !== this.value()) this.value.set(value);
  }

  private clamp(value: number): number {
    const min = this.min();
    const max = this.max();
    if (min !== null && value < min) return min;
    if (max !== null && value > max) return max;
    return value;
  }
}
